<template>
  <div class="notifications-component">
    <v-card class="notifications-card">
      <v-card-title class="d-flex justify-space-between align-center notifications-title">
        <span>All Notifications</span>
        <div>
          <v-btn
            v-if="unreadCount > 0"
            variant="outlined"
            size="small"
            color="primary"
            @click="handleMarkAllAsRead"
            :loading="markingAllAsRead"
          >
            Mark All Read
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-tabs v-model="activeTab" color="primary">
          <v-tab value="all">All ({{ notifications.length }})</v-tab>
          <v-tab value="unread">Unread ({{ unreadCount }})</v-tab>
          <v-tab value="read">Read ({{ readCount }})</v-tab>
        </v-tabs>

        <v-window v-model="activeTab" class="mt-4 notifications-window">
          <v-window-item value="all">
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="notifications.length === 0" class="empty-state">
              <v-icon size="80" class="empty-icon">mdi-bell-outline</v-icon>
              <p class="empty-text mt-4">No notifications yet</p>
            </div>
            <div v-else class="notifications-list">
              <NotificationItem
                v-for="notification in notifications"
                :key="notification.id"
                :notification="notification"
                @click="handleNotificationClick"
                @mark-read="handleMarkAsRead"
                @delete="handleDelete"
              />
            </div>
          </v-window-item>

          <v-window-item value="unread">
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="unreadNotifications.length === 0" class="empty-state">
              <v-icon size="80" class="empty-icon">mdi-check-all</v-icon>
              <p class="empty-text mt-4">No unread notifications</p>
            </div>
            <div v-else class="notifications-list">
              <NotificationItem
                v-for="notification in unreadNotifications"
                :key="notification.id"
                :notification="notification"
                @click="handleNotificationClick"
                @mark-read="handleMarkAsRead"
                @delete="handleDelete"
              />
            </div>
          </v-window-item>

          <v-window-item value="read">
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="readNotifications.length === 0" class="empty-state">
              <v-icon size="80" class="empty-icon">mdi-bell-ring-outline</v-icon>
              <p class="empty-text mt-4">No read notifications</p>
            </div>
            <div v-else class="notifications-list">
              <NotificationItem
                v-for="notification in readNotifications"
                :key="notification.id"
                :notification="notification"
                @click="handleNotificationClick"
                @mark-read="handleMarkAsRead"
                @delete="handleDelete"
              />
            </div>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import NotificationItem from './NotificationItem.vue';

export default {
  name: 'NotificationsComponent',
  components: {
    NotificationItem
  },
  data() {
    return {
      activeTab: 'all',
      markingAllAsRead: false
    };
  },
  computed: {
    ...mapGetters('notificationModule', [
      'getAllNotifications',
      'getUnreadNotifications',
      'getReadNotifications',
      'getUnreadCount',
      'notificationLoading'
    ]),
    notifications() {
      return this.getAllNotifications || [];
    },
    unreadNotifications() {
      return this.getUnreadNotifications || [];
    },
    readNotifications() {
      return this.getReadNotifications || [];
    },
    unreadCount() {
      return this.getUnreadCount || 0;
    },
    readCount() {
      return (this.readNotifications || []).length;
    },
    loading() {
      return this.notificationLoading;
    }
  },
  methods: {
    ...mapActions('notificationModule', [
      'fetchNotifications',
      'markAsRead',
      'markAllAsRead',
      'deleteNotification'
    ]),
    async loadNotifications() {
      const userInfo = this.$store.getters['CurrentUserInfo'];
      if (userInfo && userInfo.id) {
        await this.fetchNotifications(userInfo.id);
      }
    },
    async handleMarkAsRead(notificationId) {
      await this.markAsRead(notificationId);
    },
    async handleMarkAllAsRead() {
      this.markingAllAsRead = true;
      const userInfo = this.$store.getters['CurrentUserInfo'];
      if (userInfo && userInfo.id) {
        await this.$store.dispatch('notificationModule/markAllAsRead', userInfo.id);
      }
      this.markingAllAsRead = false;
    },
    async handleDelete(notificationId) {
      await this.deleteNotification(notificationId);
    },
    async handleNotificationClick(notification) {
      if (!notification.is_read) {
        await this.markAsRead(notification.id);
      }

      // Navigate based on notification type
      if (notification.type === 'NEW_REQUEST') {
        this.$router.push('/approvals');
      } else if (notification.expense_request_id) {
        this.$router.push(`/requests/${notification.expense_request_id}`);
      }
    }
  },
  async mounted() {
    await this.loadNotifications();
  }
};
</script>

<style scoped>
.notifications-component {
  width: 100%;
}

.notifications-card {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgb(var(--v-theme-border-color));
  box-shadow: 0 10px 24px rgba(var(--v-theme-on-surface-rgb), 0.08);
}

.notifications-title {
  color: rgb(var(--v-theme-on-surface));
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  color: rgba(var(--v-theme-on-surface-rgb), 0.3);
}

.empty-text {
  color: rgb(var(--v-theme-on-surface-variant));
}

.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
